import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { useAuth } from "../contexts/AuthContext";
import { WizardStage } from "../components/onboarding/WizardStage";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Check, X, Lock, Mail, AlertTriangle } from "lucide-react";
import Footer from "../components/Footer";

const RULES = [
  { key: "len", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "An uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "A lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "num", label: "A number", test: (p) => /\d/.test(p) },
  { key: "sym", label: "A symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground dark grid-bg" data-testid="self-service-provisioning">
      <div className="flex-1">
        <div className="max-w-5xl mx-auto px-6 py-10 lg:py-16">
          <div className="flex items-center gap-2.5 mb-8">
            <img src="/brand/logo_horizontal.png" alt="Lestari" className="h-8 object-contain brightness-0 invert opacity-90" />
            <Badge variant="lime" className="px-2.5 py-0.5">SELF-SERVICE PROVISIONING</Badge>
          </div>
          {children}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default function SelfServiceProvisioning() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user, login, refresh } = useAuth();

  const [phase, setPhase] = useState("loading"); // loading | invalid | expired | account | login | wizard
  const [info, setInfo] = useState(null);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  const loadSession = async () => {
    const { data } = await api.get("/onboarding/provision/session/me");
    setSession(data.session);
    return data;
  };

  useEffect(() => {
    (async () => {
      // Already signed in as a provisioning user (e.g. page refresh after login).
      if (user && !user.tenant_id && user.role !== "super_admin") {
        try {
          await loadSession();
          setPhase("wizard");
          return;
        } catch { /* fall through to token flow */ }
      }
      if (user && user.tenant_id) { navigate("/"); return; }
      if (!token) { setPhase("invalid"); return; }
      try {
        const { data } = await api.get(`/onboarding/provision/${token}`);
        setInfo(data);
        setLoginEmail(data.email || "");
        if (data.activated) { setPhase("login"); return; }
        if (data.expired) { setPhase("expired"); return; }
        setPhase(data.account_created ? "login" : "account");
      } catch (e) {
        setPhase(e.response?.status === 410 ? "expired" : "invalid");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const createAccount = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/provision/account", { token, username: username.trim(), password });
      toast.success("Account created — now sign in to continue");
      setLoginPassword("");
      setPhase("login");
    } catch (e) {
      const status = e.response?.status;
      if (status === 409) { toast.info("You already have an account — please sign in."); setPhase("login"); }
      else toast.error(apiErr(e, "Could not create your account"));
    } finally { setBusy(false); }
  };

  const doLogin = async () => {
    setBusy(true);
    try {
      const res = await login(loginEmail, loginPassword);
      if (res?.mfa_required) { toast.error("This account requires 2FA — please use the main sign-in page."); return; }
      await loadSession();
      setPhase("wizard");
    } catch (e) {
      toast.error(apiErr(e, "Sign in failed"));
    } finally { setBusy(false); }
  };

  const onActivated = async (data) => {
    localStorage.setItem("lestari_token", data.access_token);
    localStorage.setItem("lestari_user", JSON.stringify(data.user));
    toast.success("Workspace provisioned — welcome to Lestari!");
    await refresh();
    navigate("/setup?tour=1");
  };

  const rulePass = RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const pwOk = rulePass.every((r) => r.ok);

  if (phase === "loading") {
    return <Shell><div className="text-muted-foreground font-mono text-sm">Loading your provisioning link…</div></Shell>;
  }

  if (phase === "invalid" || phase === "expired") {
    return (
      <Shell>
        <div className="max-w-lg" data-testid="provision-error">
          <div className="h-12 w-12 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mb-4">
            <AlertTriangle className="h-6 w-6 text-amber-400" />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">
            {phase === "expired" ? "This link has expired" : "This link is invalid or used"}
          </h1>
          <p className="text-muted-foreground text-sm mt-3">
            {phase === "expired"
              ? "Provisioning links are valid for 7 days and can be used once. Please start a fresh trial to get a new link."
              : "This provisioning link is no longer valid — it may have already been used to create an account. If you've set a password, sign in instead."}
          </p>
          <div className="flex gap-3 mt-6">
            <Button variant="primary" onClick={() => navigate("/get-started")} data-testid="provision-restart-btn">Start a new trial</Button>
            <Button variant="outline" onClick={() => navigate("/login")} data-testid="provision-signin-btn">Sign in</Button>
          </div>
        </div>
      </Shell>
    );
  }

  if (phase === "account") {
    return (
      <Shell>
        <div className="max-w-md" data-testid="provision-account">
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome{info?.name ? `, ${info.name}` : ""} 👋</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Let's create your login for <span className="text-foreground font-semibold">{info?.company}</span>.
            Your email is set from your trial and can't be changed here.
          </p>
          <div className="card-flat bg-surface p-5 mt-6 space-y-4">
            <div>
              <Label>Email (locked)</Label>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input value={info?.email || ""} disabled data-testid="provision-email" />
              </div>
            </div>
            <div>
              <Label>Choose a username</Label>
              <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. amanda.lee" data-testid="provision-username" />
            </div>
            <div>
              <Label>Create a password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Choose a strong password" data-testid="provision-password" />
              <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="provision-password-rules">
                {rulePass.map((r) => (
                  <li key={r.key} className={`flex items-center gap-1.5 text-[11px] ${r.ok ? "text-emerald-400" : "text-muted-foreground"}`}>
                    {r.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />} {r.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Button variant="primary" size="lg" className="mt-6 gap-2" disabled={busy || !pwOk || username.trim().length < 3} onClick={createAccount} data-testid="provision-create-btn">
            <ShieldCheck className="h-4 w-4" /> {busy ? "Creating…" : "Create my login"} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </Shell>
    );
  }

  if (phase === "login") {
    return (
      <Shell>
        <div className="max-w-md" data-testid="provision-login">
          <h1 className="font-display text-3xl font-bold tracking-tight">Sign in to continue</h1>
          <p className="text-muted-foreground text-sm mt-2">
            Your account is ready. Sign in with the password you just created to provision your workspace.
          </p>
          <div className="card-flat bg-surface p-5 mt-6 space-y-4">
            <div>
              <Label>Email</Label>
              <div className="relative">
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} disabled={!!info?.email} data-testid="provision-login-email" />
              </div>
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loginPassword && doLogin()} data-testid="provision-login-password" />
            </div>
          </div>
          <Button variant="primary" size="lg" className="mt-6 gap-2" disabled={busy || !loginPassword} onClick={doLogin} data-testid="provision-login-btn">
            {busy ? "Signing in…" : "Sign in & provision"} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </Shell>
    );
  }

  // wizard
  return (
    <Shell>
      <div className="mb-2">
        <h1 className="font-display text-2xl font-bold tracking-tight">Provision your workspace</h1>
        <p className="text-muted-foreground text-sm mt-1">A few guided steps and your 15-day Lestari Lite trial is live.</p>
      </div>
      {session ? (
        <WizardStage sessionId={session.session_id} session={session} onActivated={onActivated} />
      ) : (
        <div className="text-muted-foreground font-mono text-sm">Loading your details…</div>
      )}
    </Shell>
  );
}
